import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSyncAlt,
  FaTrash,
  FaTrashRestore,
  FaUndo,
} from "react-icons/fa";
import Header from "../components/AdminHeader";

function formatDisplayDate(dateString) {
  if (!dateString) return "—";
  const d = new Date(dateString);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function normaliseRestoreRow(row, tableName = "") {
  if (!row || typeof row !== "object") return row;

  const cleaned = { ...row };

  delete cleaned.shows;
  delete cleaned.show;
  delete cleaned.profiles;
  delete cleaned.recyclebin;
  delete cleaned.__typename;

  if (tableName === "seat_bookings") {
    delete cleaned.id;
  }

  return cleaned;
}

export default function RecycleBin() {
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [toast, setToast] = useState("");
  const [workingKey, setWorkingKey] = useState("");

  const initAdminAccess = useCallback(async () => {
    try {
      setAuthLoading(true);

      const {
        data: { session: activeSession },
      } = await supabase.auth.getSession();

      if (!activeSession?.user) {
        setSession(null);
        setIsAdmin(false);
        setAuthLoading(false);
        navigate("/login", { replace: true });
        return;
      }

      setSession(activeSession);

      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("id, ADMIN")
        .eq("id", activeSession.user.id)
        .maybeSingle();

      if (profileError || !profile || profile.ADMIN !== true) {
        setIsAdmin(false);
        setAuthLoading(false);
        navigate("/home", { replace: true });
        return;
      }

      setIsAdmin(true);
      setAuthLoading(false);
    } catch (err) {
      console.error("Recycle bin access init failed:", err);
      setSession(null);
      setIsAdmin(false);
      setAuthLoading(false);
      navigate("/home", { replace: true });
    }
  }, [navigate]);

  const fetchRecycleBin = useCallback(async () => {
    setLoading(true);
    setPageError("");
    setToast("");

    const { data, error } = await supabase
      .from("recyclebin")
      .select("*")
      .eq("is_restored", false)
      .order("deleted_at", { ascending: false });

    if (error) {
      console.error("Failed to fetch recycle bin:", error);
      setItems([]);
      setPageError("Could not load recycle bin.");
      setLoading(false);
      return;
    }

    setItems(data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    initAdminAccess();
  }, [initAdminAccess]);

  useEffect(() => {
    if (!authLoading && isAdmin) {
      fetchRecycleBin();
    }
  }, [authLoading, isAdmin, fetchRecycleBin]);

  const markRestored = async (id) => {
    return supabase
      .from("recyclebin")
      .update({
        is_restored: true,
        restored_at: new Date().toISOString(),
      })
      .eq("id", id);
  };

  const restoreItem = async (item) => {
    setWorkingKey(item.id);
    setPageError("");
    setToast("");

    try {
      if (item.item_type === "booking") {
        const bookingRows = (item.payload?.bookings || []).map((row) =>
          normaliseRestoreRow(row, "seat_bookings")
        );
        const storedShow = item.payload?.show
          ? normaliseRestoreRow(item.payload.show, "shows")
          : null;

        if (bookingRows.length === 0) {
          setPageError("This booking item has no stored booking rows.");
          setWorkingKey("");
          return;
        }

        const showId = bookingRows[0]?.show_id ?? null;

        if (showId) {
          const { data: existingShow, error: existingShowError } = await supabase
            .from("shows")
            .select("id")
            .eq("id", showId)
            .maybeSingle();

          if (existingShowError) {
            console.error(
              "Failed checking linked show before booking restore:",
              existingShowError
            );
            setPageError(
              `Could not verify the booking's linked show: ${existingShowError.message}`
            );
            setWorkingKey("");
            return;
          }

          if (!existingShow && storedShow) {
            const { error: restoreShowError } = await supabase
              .from("shows")
              .insert(storedShow);

            if (restoreShowError) {
              console.error(
                "Failed to restore linked show for booking:",
                restoreShowError
              );
              setPageError(
                `Could not restore the linked show needed for this booking: ${restoreShowError.message}`
              );
              setWorkingKey("");
              return;
            }
          }
        }

        const { error: restoreBookingsError } = await supabase
          .from("seat_bookings")
          .insert(bookingRows);

        if (restoreBookingsError) {
          console.error("Failed to restore booking rows:", restoreBookingsError);
          setPageError(
            `Could not restore this booking: ${restoreBookingsError.message}`
          );
          setWorkingKey("");
          return;
        }

        const { error: markError } = await markRestored(item.id);

        if (markError) {
          console.error("Failed to mark recycle item restored:", markError);
          setPageError(
            `Booking restored, but recycle bin state could not be updated: ${markError.message}`
          );
          setWorkingKey("");
          return;
        }

        setToast("Booking restored.");
      }

      if (item.item_type === "show") {
        const showRow = item.payload?.show
          ? normaliseRestoreRow(item.payload.show, "shows")
          : null;
        const bookingRows = (item.payload?.bookings || []).map((row) =>
          normaliseRestoreRow(row, "seat_bookings")
        );

        if (!showRow) {
          setPageError("This show item has no stored show row.");
          setWorkingKey("");
          return;
        }

        const { data: existingShow, error: existingShowError } = await supabase
          .from("shows")
          .select("id")
          .eq("id", showRow.id)
          .maybeSingle();

        if (existingShowError) {
          console.error("Failed checking existing show:", existingShowError);
          setPageError(
            `Could not verify the show before restore: ${existingShowError.message}`
          );
          setWorkingKey("");
          return;
        }

        if (!existingShow) {
          const { error: restoreShowError } = await supabase
            .from("shows")
            .insert(showRow);

          if (restoreShowError) {
            console.error("Failed to restore show:", restoreShowError);
            setPageError(
              `Could not restore this show: ${restoreShowError.message}`
            );
            setWorkingKey("");
            return;
          }
        }

        if (bookingRows.length > 0) {
          const { error: restoreBookingsError } = await supabase
            .from("seat_bookings")
            .insert(bookingRows);

          if (restoreBookingsError) {
            console.error(
              "Failed to restore show bookings:",
              restoreBookingsError
            );
            setPageError(
              `Show restored, but linked bookings could not be restored: ${restoreBookingsError.message}`
            );
            setWorkingKey("");
            await fetchRecycleBin();
            return;
          }
        }

        const { error: markError } = await markRestored(item.id);

        if (markError) {
          console.error("Failed to mark recycle item restored:", markError);
          setPageError(
            `Show restored, but recycle bin state could not be updated: ${markError.message}`
          );
          setWorkingKey("");
          return;
        }

        setToast("Show restored.");
      }

      setWorkingKey("");
      await fetchRecycleBin();
    } catch (error) {
      console.error("Restore failed:", error);
      setPageError(
        `Something went wrong while restoring this item: ${
          error.message || "Unknown error"
        }`
      );
      setWorkingKey("");
    }
  };

  const deleteItemForever = async (item) => {
    const confirmed = window.confirm(
      `Permanently delete "${item.title}" from the recycle bin?`
    );
    if (!confirmed) return;

    setWorkingKey(item.id);
    setPageError("");
    setToast("");

    const { error } = await supabase
      .from("recyclebin")
      .delete()
      .eq("id", item.id);

    if (error) {
      console.error("Failed to permanently delete recycle item:", error);
      setPageError(`Could not permanently delete this item: ${error.message}`);
      setWorkingKey("");
      return;
    }

    setToast("Item permanently deleted.");
    setWorkingKey("");
    await fetchRecycleBin();
  };

  const clearAll = async () => {
    const confirmed = window.confirm(
      "Permanently delete all items currently in the recycle bin?"
    );
    if (!confirmed) return;

    setWorkingKey("clear-all");
    setPageError("");
    setToast("");

    const activeIds = items.map((item) => item.id);

    if (activeIds.length === 0) {
      setToast("Recycle bin is already empty.");
      setWorkingKey("");
      return;
    }

    const { error } = await supabase
      .from("recyclebin")
      .delete()
      .in("id", activeIds);

    if (error) {
      console.error("Failed to clear recycle bin:", error);
      setPageError(`Could not clear recycle bin: ${error.message}`);
      setWorkingKey("");
      return;
    }

    setToast("Recycle bin cleared.");
    setWorkingKey("");
    await fetchRecycleBin();
  };

  if (authLoading) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#08111f] text-white">
        Checking admin access...
      </div>
    );
  }

  if (!session || !isAdmin) {
    return null;
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-[#08111f] text-white">
        <div className="pointer-events-none fixed inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(135deg,#4a0454_0%,#3c2f73_35%,#255a86_68%,#12728a_100%)] opacity-90" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.08),transparent_26%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.05),transparent_24%)]" />
          <div className="absolute inset-0 bg-black/30" />
        </div>

        <div className="relative mx-auto max-w-[1600px] px-4 py-5 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
          <section className="mb-8 overflow-hidden rounded-[30px] border border-white/10 bg-[linear-gradient(135deg,rgba(86,24,113,0.45),rgba(39,78,125,0.45),rgba(18,111,135,0.40))] shadow-[0_20px_80px_rgba(0,0,0,0.32)] backdrop-blur-xl">
            <div className="flex flex-col gap-6 px-5 py-6 sm:px-8 sm:py-8 lg:px-10 lg:py-10">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[11px] font-medium uppercase tracking-[0.24em] text-white/80">
                    <FaTrashRestore />
                    <span>Recycle Bin</span>
                  </div>

                  <h1 className="text-3xl font-semibold tracking-tight sm:text-5xl">
                    Recycle Bin
                  </h1>

                  <p className="mt-4 max-w-3xl text-sm leading-7 text-white/80 sm:text-base lg:text-lg">
                    Restore deleted bookings and shows, or permanently clear them.
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => navigate("/admin")}
                    className="inline-flex h-12 items-center justify-center gap-3 rounded-2xl border border-white/10 bg-white/[0.08] px-5 text-sm font-semibold text-white transition hover:bg-white/[0.12]"
                  >
                    <FaArrowLeft />
                    <span>Back to Admin</span>
                  </button>

                  <button
                    type="button"
                    onClick={fetchRecycleBin}
                    className="inline-flex h-12 items-center justify-center gap-3 rounded-2xl border border-white/10 bg-white/[0.08] px-5 text-sm font-semibold text-white transition hover:bg-white/[0.12]"
                  >
                    <FaSyncAlt />
                    <span>Refresh</span>
                  </button>

                  <button
                    type="button"
                    onClick={clearAll}
                    disabled={workingKey === "clear-all" || items.length === 0}
                    className="inline-flex h-12 items-center justify-center gap-3 rounded-2xl border border-red-400/20 bg-red-500/10 px-5 text-sm font-semibold text-red-100 transition hover:bg-red-500/20 disabled:opacity-60"
                  >
                    <FaTrash />
                    <span>
                      {workingKey === "clear-all" ? "Clearing..." : "Clear All"}
                    </span>
                  </button>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-2xl border border-white/10 bg-white/[0.08] p-4">
                  <div className="text-xs uppercase tracking-[0.24em] text-white/55">
                    Items in bin
                  </div>
                  <div className="mt-2 text-xl font-semibold text-white">
                    {items.length}
                  </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/[0.08] p-4">
                  <div className="text-xs uppercase tracking-[0.24em] text-white/55">
                    Shows
                  </div>
                  <div className="mt-2 text-xl font-semibold text-white">
                    {items.filter((item) => item.item_type === "show").length}
                  </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/[0.08] p-4">
                  <div className="text-xs uppercase tracking-[0.24em] text-white/55">
                    Bookings
                  </div>
                  <div className="mt-2 text-xl font-semibold text-white">
                    {items.filter((item) => item.item_type === "booking").length}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {pageError ? (
            <div className="mb-4 flex items-start gap-3 rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-4 text-sm text-red-100">
              <FaExclamationTriangle className="mt-0.5 shrink-0" />
              <span>{pageError}</span>
            </div>
          ) : null}

          {toast ? (
            <div className="mb-4 flex items-start gap-3 rounded-2xl border border-emerald-400/20 bg-emerald-500/10 px-4 py-4 text-sm text-emerald-100">
              <FaCheckCircle className="mt-0.5 shrink-0" />
              <span>{toast}</span>
            </div>
          ) : null}

          <section className="rounded-[30px] border border-white/10 bg-[rgba(17,26,43,0.72)] p-4 shadow-[0_18px_60px_rgba(0,0,0,0.30)] backdrop-blur-xl sm:p-6 lg:p-7">
            {loading ? (
              <div className="grid min-h-[260px] place-items-center rounded-[24px] border border-white/10 bg-white/[0.04] text-sm text-white/70">
                Loading recycle bin...
              </div>
            ) : items.length === 0 ? (
              <div className="rounded-[24px] border border-white/10 bg-white/[0.04] px-5 py-12 text-center text-sm text-white/70">
                Recycle bin is empty.
              </div>
            ) : (
              <div className="space-y-4">
                {items.map((item) => {
                  const bookingCount = item.payload?.bookings?.length || 0;
                  const showName = item.payload?.show?.show_name || "Unknown Show";

                  return (
                    <div
                      key={item.id}
                      className="rounded-[24px] border border-white/10 bg-white/[0.04] p-5"
                    >
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0">
                          <div className="mb-2 flex flex-wrap items-center gap-2">
                            <span
                              className={`inline-flex rounded-full border px-3 py-1 text-xs font-medium ${
                                item.item_type === "show"
                                  ? "border-cyan-300/20 bg-cyan-400/10 text-cyan-100"
                                  : "border-violet-300/20 bg-violet-400/10 text-violet-100"
                              }`}
                            >
                              {item.item_type === "show" ? "Show" : "Booking"}
                            </span>

                            <span className="inline-flex rounded-full border border-white/10 bg-white/[0.06] px-3 py-1 text-xs font-medium text-white/80">
                              Deleted {formatDisplayDate(item.deleted_at)}
                            </span>
                          </div>

                          <h3 className="text-xl font-semibold text-white">
                            {item.title}
                          </h3>

                          <div className="mt-3 space-y-1 text-sm text-white/70">
                            <div>
                              <span className="font-medium text-white/90">Source:</span>{" "}
                              {item.source_table}
                            </div>
                            <div>
                              <span className="font-medium text-white/90">Source Key:</span>{" "}
                              {item.source_key}
                            </div>

                            {item.item_type === "show" ? (
                              <>
                                <div>
                                  <span className="font-medium text-white/90">
                                    Show Name:
                                  </span>{" "}
                                  {showName}
                                </div>
                                <div>
                                  <span className="font-medium text-white/90">
                                    Linked Bookings Stored:
                                  </span>{" "}
                                  {bookingCount}
                                </div>
                              </>
                            ) : (
                              <div>
                                <span className="font-medium text-white/90">
                                  Booking Rows Stored:
                                </span>{" "}
                                {bookingCount}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-3">
                          <button
                            type="button"
                            onClick={() => restoreItem(item)}
                            disabled={workingKey === item.id}
                            className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl border border-emerald-400/20 bg-emerald-500/10 px-4 text-sm font-semibold text-emerald-100 transition hover:bg-emerald-500/20 disabled:opacity-60"
                          >
                            <FaUndo />
                            <span>
                              {workingKey === item.id ? "Restoring..." : "Restore"}
                            </span>
                          </button>

                          <button
                            type="button"
                            onClick={() => deleteItemForever(item)}
                            disabled={workingKey === item.id}
                            className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl border border-red-400/20 bg-red-500/10 px-4 text-sm font-semibold text-red-100 transition hover:bg-red-500/20 disabled:opacity-60"
                          >
                            <FaTrash />
                            <span>Delete Permanently</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>
    </>
  );
}